import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChange,
	ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { HeaderConfigService } from "@obg/core.header";
import { HomeConfigService } from "@obg/core.home";
import { RegulatoryBarService } from "@obg/core.regulatory-bar";
import { AamsGameDataContextState, AamsGameDataSelector, AamsGameDataState } from "@obg/gaming.aams-game-data";
import { KycNotification, KycNotificationService } from "@obg/mat.ui.common";
import { CustomerValidationConfig, CommonState, IauiVerificationSelector } from "@obg/ngx.common";
import {
	AllowedAction,
	AllowedActions,
	AppContextSelector,
	AuthSelector,
	CORE_ROUTE,
	CoreState,
	CoreUiInteractionEvent,
	CustomerComplianceService,
	CustomerManagementSelector,
	DisallowedActionCodes,
	Jurisdiction,
	JurisdictionSelector,
	KycState,
	RequiredAction,
	RequiredActions,
	ResponsibleGamingConfigService,
	STARTUP_CONTEXT_TOKEN,
	StartupContext,
	UiInteraction,
	UiInteractionAction,
} from "@obg/ngx.core";
import { CoreContentState, Icon, IconSelector, MenuItem, MenuSelector } from "@obg/ngx.core.content";
import { FeatureFlagService, ignoreNil, isLocalStorageSupported } from "@obg/ngx.global";
import { coerceBoolean } from "@obg/ngx.global.evo";
import { RouteAction, RouteLinkActiveOptions, RouteLinkService, RouteService, RouteSelector } from "@obg/ngx.router";
import { subMonths } from "date-fns";
import { combineLatest, merge, Observable, of, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, mergeMap, switchMap, tap } from "rxjs/operators";

import { DynamicUiService } from "../dynamic-ui/dynamic-ui.service";

import {
	ANNUAL_VALIDATION_HEADER_HEIGHT,
	DEFAULT_BRAND_LOGO_KEY,
	DOCUMENT_PROCESSING_HEADER_HEIGHT,
	DOCUMENT_VALIDATION_HEADER_HEIGHT,
	HEADER_HEIGHT,
	HIDE_KYC_NOTIFICATION_STORAGE_KEY,
	IAUI_NOTIFICATION_HEADER_HEIGHT,
	KYC_HEADER_HEIGHT,
	TIMER_HEADER_HEIGHT,
} from "./header.container.const";
import { HeaderAction, TrackingContext } from "./tracking.model";

const HEADER_EVENT: UiInteraction = {
	event: CoreUiInteractionEvent.Navigate,
	context: TrackingContext.HeaderInteractionContext,
};
@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: "header[obg-app-m-header-container]",
	templateUrl: "./header.container.html",
	styleUrls: ["./header.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class HeaderContainer implements OnInit, OnDestroy, OnChanges {
	@Input()
	@coerceBoolean()
	balanceHidden = false;

	@Input()
	@coerceBoolean()
	toolbarHidden = false;

	@Input()
	@coerceBoolean()
	navigationHidden = false;

	@Input()
	@coerceBoolean()
	regulatoryBarOnly = false;

	@Input()
	homeRoute: string | undefined;

	@HostBinding("class.obg-m-header") hostCssClass = true;

	@Input() titleText: string | undefined;

	@Output() search = new EventEmitter<void>();

	navigationBarData$: Observable<{ productMenu: MenuItem[]; icons: Icon[] }> | undefined;

	isSearchOnHomePageEnabled$: Observable<boolean>;

	brandLogo$: Observable<string> | undefined;

	sbSitemapRoute$: Observable<string> | undefined;

	showValidationNotification$: Observable<boolean> | undefined;

	logoTrackingData: UiInteraction = { ...HEADER_EVENT, data: { ItemClicked: HeaderAction.Logo } };
	helpTrackingData: UiInteraction = { ...HEADER_EVENT, data: { ItemClicked: HeaderAction.Help } };

	linkActiveOptions: RouteLinkActiveOptions | undefined;

	showKycNotification: boolean | undefined;
	kycHeaderHeight = KYC_HEADER_HEIGHT;
	showLanguageSelector$ = this.headerConfig.isHeaderLanguageSelectorVisible$();
	showTicketCounter = this.headerConfig.isTicketCounterEnabled();
	showSponsorshipIconList = this.headerConfig.isSponsorshipIconListVisible();
	isLoginEnabled = this.headerConfig.isLoginEnabled();
	isRegulatoryBarEnabled = false;
	isAnnualValidationNotificationEnabled = false;
	isDocumentValidationEnabled = false;
	isSocialBenefitsRestrictionEnabled = false;
	isDocumentProcessing = false;
	showIauiNotification = false;
	notificationData: KycNotification | undefined;
	isAuthenticated = false;
	isRegulatoryBarEnabledForLoggedOut = false;
	regulatoryBarDynamicHeight: number | undefined;
	isAamsWithoutGamingSession = false;
	showSportsbookSitemap = this.startupContext.isBot && this.headerConfig.isSportsbookSitemapEnabled();
	aamsGameData: AamsGameDataContextState | undefined;
	shouldShowAamsGamingSession = true;
	actionExpired = false;

	searchTrackingData: UiInteraction = {
		event: CoreUiInteractionEvent.Navigate,
		context: "toolbar-interaction-context",
		data: { ItemClicked: "Search" },
	};

	private regulatoryBarHeight = TIMER_HEADER_HEIGHT;
	private headerHeight = HEADER_HEIGHT;
	private data$$: Subscription | undefined;
	private socialBenefitsRestrictionFeatureFlagEnabled = false;

	readonly searchPlaceholder = "game-search.placeholder";

	private readonly annualValidationHeaderHeight = ANNUAL_VALIDATION_HEADER_HEIGHT;
	private readonly documentProcessingHeaderHeight = DOCUMENT_PROCESSING_HEADER_HEIGHT;
	private readonly iauiNotificationHeaderHeight = IAUI_NOTIFICATION_HEADER_HEIGHT;
	private readonly documentValidationHeaderHeight = DOCUMENT_VALIDATION_HEADER_HEIGHT;

	constructor(
		@Inject(STARTUP_CONTEXT_TOKEN) private startupContext: StartupContext,
		private responsibleGamingConfigService: ResponsibleGamingConfigService,
		private store: Store<CoreContentState & CommonState & CoreState & AamsGameDataState>,
		private iconSelector: IconSelector,
		private uiInteractionAction: UiInteractionAction,
		private routeLinkService: RouteLinkService,
		private dynamicUiService: DynamicUiService,
		private changeDetector: ChangeDetectorRef,
		private headerConfig: HeaderConfigService,
		private customerComplianceService: CustomerComplianceService,
		private customerValidationConfig: CustomerValidationConfig,
		private iauiVerificationSelector: IauiVerificationSelector,
		private kycNotificationService: KycNotificationService,
		private regulatoryBarService: RegulatoryBarService,
		private routeService: RouteService,
		private aamsGameDataSelector: AamsGameDataSelector,
		private homeConfigService: HomeConfigService,
		private featureFlagService: FeatureFlagService
	) {
		this.isSearchOnHomePageEnabled$ = this.store.select(RouteSelector.getCurrent).pipe(
			map((route) => {
				return route?.name === CORE_ROUTE.home && this.homeConfigService.getIsSearchOnHomePageEnabled();
			})
		);
	}

	ngOnInit() {
		this.homeRoute = this.homeRoute || CORE_ROUTE.home;

		this.socialBenefitsRestrictionFeatureFlagEnabled = this.isSocialBenefitsRestrictionFeatureFlagActive();

		if (this.showSportsbookSitemap) {
			this.sbSitemapRoute$ = this.routeService.generateUrl$({ routeName: "sitemap-sportsbook" });
		}

		this.navigationBarData$ = combineLatest([
			this.store.select(MenuSelector.get("product-menu")),
			this.store.select(this.iconSelector.get("sponsorshipiconsheader")),
		]).pipe(
			map(([products, icons]) => {
				const productMenu = products?.items.slice(0, 13) ?? [];
				this.linkActiveOptions = {
					urlsToMatchFrom: productMenu
						.map((item) =>
							this.routeLinkService.getExternalUrlFromRouteLink({
								url: item.link?.url,
								route: item.route,
							})
						)
						.filter((url) => url !== undefined) as string[],
				};
				return { productMenu, icons: icons?.slice(0, 3) ?? [] };
			})
		);

		this.brandLogo$ = this.store.select(AppContextSelector.getProduct).pipe(
			ignoreNil(),
			map((product) => this.headerConfig.getCustomBrandLogoByProductType()[product] ?? DEFAULT_BRAND_LOGO_KEY),
			distinctUntilChanged()
		);

		this.showValidationNotification$ = this.validationSelector$();

		const isAamsWithoutGamingSession$ = combineLatest([
			this.store.select(AuthSelector.isAuthenticated),
			this.store.select(JurisdictionSelector.get),
		]).pipe(
			filter(
				([isAuthenticated, jurisdiction]) =>
					(this.isAamsWithoutGamingSession = jurisdiction === Jurisdiction.Aams) && isAuthenticated
			),
			switchMap(() => this.store.select(this.aamsGameDataSelector.getAll())),
			ignoreNil(),
			tap((aamsGameData) => {
				this.aamsGameData = aamsGameData;
				this.isAamsWithoutGamingSession = !Object.keys(aamsGameData.activeGame).length;
			})
		);

		const shouldShowAamsSession$ = this.store
			.select(this.aamsGameDataSelector.isSessionDisplayed())
			.pipe(tap((shouldShow) => (this.shouldShowAamsGamingSession = shouldShow)));

		const updateHeaders$ = this.store
			.select(AuthSelector.isAuthenticated)
			.pipe(tap((isAuth) => (this.isAuthenticated = isAuth)));

		const showKycNotification$ = this.customerComplianceService.getCurrentCompliance$().pipe(
			mergeMap((compliance) => {
				const kycNotification = this.kycNotificationService.getKycNotificationType(compliance);
				if (!kycNotification) {
					this.showKycNotification = false;
					return of(undefined);
				}
				this.showKycNotification =
					!isLocalStorageSupported() || localStorage.getItem(HIDE_KYC_NOTIFICATION_STORAGE_KEY) !== "1";
				this.notificationData = kycNotification;
				return this.kycNotificationService.notificationIsExpired(this.notificationData);
			}),
			tap((notificationIsExpired) => {
				if (!notificationIsExpired) {
					return;
				}
				this.showKycNotification = false;
				this.notificationData = undefined;
				this.changeDetector.markForCheck();
			})
		);

		const verificationData$ = this.store.pipe(
			this.iauiVerificationSelector.showNotification$(),
			tap((showNotification) => (this.showIauiNotification = showNotification))
		);

		const regulatoryBarEnabledForloggedOut$ = this.responsibleGamingConfigService
			.getRegulatoryBarEnabledForLoggedOut$()
			.pipe(
				tap(
					(regulatoryBarEnabledForLoggedOut) =>
						(this.isRegulatoryBarEnabledForLoggedOut = regulatoryBarEnabledForLoggedOut ?? false)
				)
			);

		const regulatoryBarDynamicHeight$ = this.regulatoryBarService
			.regulatoryBarDynamicHeight$()
			.pipe(tap((dynamicHeight) => (this.regulatoryBarDynamicHeight = dynamicHeight)));

		const regulatoryBarDynamicHeightByJurisdiction$ = this.store.select(JurisdictionSelector.get).pipe(
			ignoreNil(),
			filter((jurisdiction) => jurisdiction !== Jurisdiction.Aams),
			tap(() => this.regulatoryBarService.setRegulatoryBarDynamicHeight(undefined))
		);

		this.data$$ = merge(
			showKycNotification$,
			verificationData$,
			updateHeaders$,
			regulatoryBarEnabledForloggedOut$,
			regulatoryBarDynamicHeight$,
			regulatoryBarDynamicHeightByJurisdiction$,
			isAamsWithoutGamingSession$,
			shouldShowAamsSession$,
			this.calculateAnnualValidationNotification$(),
			this.showValidationNotification$
		).subscribe(() => this.updateHeaders());
	}

	ngOnChanges(changes: { regulatoryBarOnly: SimpleChange }) {
		if (changes.regulatoryBarOnly) {
			this.updateHeaders();
		}
	}

	ngOnDestroy() {
		if (this.data$$) {
			this.data$$.unsubscribe();
		}
	}

	openSearch(): void {
		this.search.emit();
	}

	menuItemSelected(menuItemClicked: MenuItem) {
		this.store.dispatch(
			this.uiInteractionAction.emit({
				event: CoreUiInteractionEvent.Navigate,
				context: TrackingContext.MainNavigationInteractionContext,
				data: { ItemClicked: menuItemClicked.title },
			})
		);
		this.store.dispatch(RouteAction.setDataForSubArea({ subArea: "Product Lobby Page" }));
	}

	dismissNotification() {
		this.showValidationNotification$ = of(false);
		sessionStorage.setItem("notificationDismissed", "true");
		this.dynamicUiService.setHeaderHeight(
			(this.regulatoryBarOnly ? 0 : this.headerHeight) +
				(this.isRegulatoryBarEnabled ? this.regulatoryBarHeight : 0) +
				(this.showKycNotification ? this.kycHeaderHeight : 0) +
				(this.showIauiNotification ? this.iauiNotificationHeaderHeight : 0)
		);
	}

	private validationSelector$(): Observable<boolean> {
		const annualValidationEnabled = this.customerValidationConfig.getAnnualValidationNotificationEnabled();
		const documentValidationEnabled = this.customerValidationConfig.getDocumentValidationNotificationEnabled();

		if (annualValidationEnabled && documentValidationEnabled) {
			return this.customerValidationNotificationVisibility();
		}

		if (annualValidationEnabled) {
			return this.getValidationNotificationVisibility(RequiredActions.AccountDataValidation);
		}

		if (documentValidationEnabled) {
			return this.getValidationNotificationVisibility(RequiredActions.DocumentUpload);
		}

		return of(false);
	}

	private getValidationNotificationVisibility(requiredAction: RequiredActions): Observable<boolean> {
		const isAuthenticated$ = this.store.select(AuthSelector.isAuthenticated);
		const requiredActions$ = this.store.select(CustomerManagementSelector.getRequiredActions);
		const allowedActions$ = this.store.select(CustomerManagementSelector.getAllowedAction);
		const kycStatus$ = this.store.select(CustomerManagementSelector.getKYCStatus);

		return combineLatest([isAuthenticated$, requiredActions$, allowedActions$, kycStatus$]).pipe(
			map(([isAuthenticated, requiredActions, allowedActions, kycStatus]) => {
				if (!isAuthenticated || !requiredActions) {
					return false;
				}

				if (!requiredActions.length && kycStatus === KycState.None) {
					this.isDocumentProcessing = true;
					return true;
				}

				this.isDocumentProcessing = this.getDocumentProcessing(allowedActions);

				const validationAction = requiredActions.find((action) => action.actionType === requiredAction);

				if (validationAction) {
					if (requiredAction === RequiredActions.AccountDataValidation) {
						this.isAnnualValidationNotificationEnabled = true;
						return this.isActionNotificationVisible(validationAction);
					}
					this.isDocumentValidationEnabled = true;
					return this.isActionNotificationVisible(validationAction, this.isDocumentProcessing);
				}

				return false;
			})
		);
	}

	private customerValidationNotificationVisibility(): Observable<boolean> {
		const isAuthenticated$ = this.store.select(AuthSelector.isAuthenticated);
		const requiredActions$ = this.store.select(CustomerManagementSelector.getRequiredActions);
		const allowedActions$ = this.store.select(CustomerManagementSelector.getAllowedAction);
		const kycStatus$ = this.store.select(CustomerManagementSelector.getKYCStatus);
		const nationalSocialBenefit$ = this.socialBenefitsRestrictionFeatureFlagEnabled
			? this.store.select(CustomerManagementSelector.getNationalSocialBenefit)
			: of(undefined);

		return combineLatest([
			isAuthenticated$,
			requiredActions$,
			allowedActions$,
			kycStatus$,
			nationalSocialBenefit$,
		]).pipe(
			map(([isAuthenticated, requiredActions, allowedActions, kycStatus, nationalSocialBenefit]) => {
				this.isAnnualValidationNotificationEnabled = false;
				this.isDocumentValidationEnabled = false;
				this.isSocialBenefitsRestrictionEnabled = false;

				if (!isAuthenticated || !requiredActions) {
					return false;
				}

				// Takes highest priority over all other notifications due to regulatory requirements for social benefit holders
				if (this.socialBenefitsRestrictionFeatureFlagEnabled && nationalSocialBenefit?.isSocialBenefitHolder) {
					this.isSocialBenefitsRestrictionEnabled = true;
					return true;
				}

				if (
					!requiredActions?.find((action) => action.actionType === RequiredActions.DocumentUpload) &&
					kycStatus === KycState.None
				) {
					this.isDocumentProcessing = true;
					return true;
				}

				this.isDocumentProcessing = this.getDocumentProcessing(allowedActions);

				const accountDataValidationAction = requiredActions.find(
					(action) => action.actionType === RequiredActions.AccountDataValidation
				);

				const documentValidation = requiredActions.find(
					(action) => action.actionType === RequiredActions.DocumentUpload
				);

				if (accountDataValidationAction && documentValidation) {
					const annualActiveFromDate =
						new Date(accountDataValidationAction.activeFrom).getTime() - new Date().getTime();
					const documentActiveFromDate =
						new Date(documentValidation.activeFrom).getTime() - new Date().getTime();

					if (annualActiveFromDate <= documentActiveFromDate) {
						this.isAnnualValidationNotificationEnabled = true;
						return this.isActionNotificationVisible(accountDataValidationAction);
					}
					if (this.isDocumentProcessing && this.isActionNotificationVisible(accountDataValidationAction)) {
						this.isAnnualValidationNotificationEnabled = true;
						return true;
					}
					this.isDocumentValidationEnabled = true;
					return this.isActionNotificationVisible(documentValidation, this.isDocumentProcessing);
				}

				if (accountDataValidationAction) {
					this.isAnnualValidationNotificationEnabled = true;
					return this.isActionNotificationVisible(accountDataValidationAction);
				}

				if (documentValidation) {
					this.isDocumentValidationEnabled = true;
					return this.isActionNotificationVisible(documentValidation, this.isDocumentProcessing);
				}
				return false;
			})
		);
	}

	private isSocialBenefitsRestrictionFeatureFlagActive(): boolean {
		const featureFlag = this.featureFlagService.get<{ enabled?: boolean } | undefined>(
			"pax_social_benefits_restriction"
		);

		return !!featureFlag?.enabled;
	}

	private getDocumentProcessing(allowedActions?: AllowedAction): boolean {
		if (
			Object.values(AllowedActions).some((action) =>
				allowedActions?.[action]?.disallowedReasons.includes(
					DisallowedActionCodes.M_CURRENTCUSTOMER_DOCUMENTVERIFICATIONPROCESSING
				)
			)
		) {
			return true;
		}
		return false;
	}

	private isActionNotificationVisible(requiredAction: RequiredAction, isProcessing?: boolean) {
		if (isProcessing) {
			if (sessionStorage.getItem("notificationDismissed") === "true") {
				return false;
			}
			return true;
		}

		const activeFromDate = new Date(requiredAction.activeFrom);
		const reminderStart = subMonths(activeFromDate, 1);
		const currentDate = new Date();

		this.actionExpired = activeFromDate < currentDate;
		if (!this.actionExpired && sessionStorage.getItem("notificationDismissed") === "true") {
			return false;
		}
		return currentDate >= reminderStart;
	}

	private calculateAnnualValidationNotification$(): Observable<boolean> {
		return combineLatest([this.showValidationNotification$ ?? of(false)]).pipe(
			map(([showNotification]) => {
				return (
					(this.isSocialBenefitsRestrictionEnabled ||
						this.isAnnualValidationNotificationEnabled ||
						this.isDocumentValidationEnabled ||
						this.isDocumentProcessing) &&
					showNotification
				);
			})
		);
	}

	private updateHeaders() {
		this.isRegulatoryBarEnabled = this.responsibleGamingConfigService.getRegulatoryBarEnabled();

		if (this.regulatoryBarDynamicHeight !== undefined) {
			if (this.regulatoryBarOnly && (this.isAamsWithoutGamingSession || !this.shouldShowAamsGamingSession)) {
				this.regulatoryBarHeight = 0;
			} else {
				this.regulatoryBarHeight = this.regulatoryBarDynamicHeight;
			}
		} else {
			this.regulatoryBarHeight =
				!this.isAuthenticated && !this.isRegulatoryBarEnabledForLoggedOut ? 0 : TIMER_HEADER_HEIGHT;
		}

		// eslint-disable-next-line rxjs/no-ignored-subscription
		this.calculateAnnualValidationNotification$().subscribe((shouldShowAnnualValidationNotification) => {
			this.dynamicUiService.setHeaderHeight(
				(this.regulatoryBarOnly ? 0 : this.headerHeight) +
					(this.isRegulatoryBarEnabled ? this.regulatoryBarHeight : 0) +
					(this.showKycNotification ? this.kycHeaderHeight : 0) +
					(shouldShowAnnualValidationNotification ? this.validationHeaderSizeCalculator() : 0) +
					(this.showIauiNotification ? this.iauiNotificationHeaderHeight : 0)
			);
		});

		this.regulatoryBarService.setAamsRegulatoryBarEnabled(!this.regulatoryBarOnly);
		this.changeDetector.markForCheck();
	}

	private validationHeaderSizeCalculator(): number {
		if (this.isSocialBenefitsRestrictionEnabled) {
			return this.annualValidationHeaderHeight;
		}

		if (this.isAnnualValidationNotificationEnabled) {
			return this.annualValidationHeaderHeight;
		}

		if (this.isDocumentProcessing) {
			return this.documentProcessingHeaderHeight;
		}

		if (this.isDocumentValidationEnabled) {
			return this.documentValidationHeaderHeight;
		}
		return 0;
	}

	hideKyc() {
		if (isLocalStorageSupported()) {
			localStorage.setItem(HIDE_KYC_NOTIFICATION_STORAGE_KEY, "1");
		}
		this.showKycNotification = false;
		this.updateHeaders();
	}
}
